import { useState, useEffect, useCallback } from "react";
import { FileSpreadsheet, FileText, Loader2, AlertTriangle, RefreshCw, Eye, Receipt } from "lucide-react";
import { Link } from "react-router-dom";
import { exportarTablaPdf, fechaReporte } from "../utils/reportes";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { toast } from "sonner";
import { FacturaVistaModal } from "../components/FacturacionVentas";   // Fix FP

// Fix DS (2026-09-22): el Libro de Ventas sale de las cobranzas ya emitidas, que
// es donde viven las certificaciones facturadas al cliente.
// Fix HP (2026-10-22): "todos los datos que llegan aquí deben ser solo informe, nada a ser modificado. Todo debe ser
// cargado en Facturación y cobros" (CEN, Claudio). El Libro es solo de consulta: no se carga, emite ni modifica nada acá.

const COLS = [
  { k: "fecha", l: "FECHA" },
  { k: "nro_factura", l: "NRO FACTURA", mono: true },
  { k: "timbrado", l: "TIMBRADO", mono: true },
  { k: "ruc", l: "RUC", mono: true },
  { k: "razon_social", l: "RAZÓN SOCIAL" },
  { k: "obra", l: "OBRA" },
  { k: "grav_10", l: "GRAV. 10%", num: true },
  { k: "iva_10", l: "IVA 10%", num: true },
  { k: "exento", l: "EXENTO", num: true },
  { k: "total", l: "TOTAL", num: true },
  // Fix GK (2026-10-16): "Al libro de Ventas del ERP agregarle una columna [Retenc.] donde se irá
  // almacenando las retenciones de IVA" (CEN). Se carga en Tesorería › Cobranzas (Fix GL).
  { k: "retencion_iva", l: "RETENC.", num: true },
  { k: "condicion", l: "CONDICIÓN" },
  // Fix EA (2026-09-23): "ventas solo debe figurar la facturación, ya sea contado o crédito
  // y en otro apartado ya sería la parte de pago": el estado del cobro queda en Cobranzas.
];

function fmt(n) {
  // Fix DU (2026-09-22): un cero se muestra como guión, igual que en el resto del ERP.
  if (!n) return "—";
  return Number(n).toLocaleString("es-PY", { maximumFractionDigits: 0 });
}

export default function ContabilidadLibroVentas() {
  const [filas, setFilas] = useState([]);
  const [totales, setTotales] = useState({ grav_10: 0, iva_10: 0, exento: 0, total: 0, retencion_iva: 0 });   // Fix GK
  const [sinDatos, setSinDatos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [filtros, setFiltros] = useState({ desde: "", hasta: "", nro_factura: "" });
  const [vistaFactura, setVistaFactura] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;
      if (filtros.nro_factura) params.nro_factura = filtros.nro_factura;
      const { data } = await axios.get(`${API}/contabilidad/libro-ventas`, { params, withCredentials: true });
      setFilas(data?.filas || []);
      setTotales(data?.totales || { grav_10: 0, iva_10: 0, exento: 0, total: 0, retencion_iva: 0 });   // Fix GK
      setSinDatos(data?.sin_datos_fiscales || 0);
    } catch { toast.error("Error al cargar el libro de ventas"); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { load(); }, [load]);

  const exportar = async () => {
    setExportando(true);
    try {
      const params = new URLSearchParams();
      if (filtros.desde) params.append("desde", filtros.desde);
      if (filtros.hasta) params.append("hasta", filtros.hasta);
      if (filtros.nro_factura) params.append("nro_factura", filtros.nro_factura);
      const res = await axios.get(`${API}/contabilidad/libro-ventas/export-excel?${params}`,
        { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Libro_Ventas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Excel generado");
    } catch { toast.error("Error al exportar el Excel"); }
    finally { setExportando(false); }
  };

  // Fix EB (2026-09-23): PDF con los mismos filtros que la tabla y el Excel.
  const exportarPdf = (ver = false) => {   // Fix FO: ver = vista previa en pantalla
    if (!filas.length) { toast.error("No hay ventas para exportar con esos filtros"); return; }
    exportarTablaPdf({
      titulo: "Libro de Ventas",
      filtros: [filtros.desde && `Desde ${fechaReporte(filtros.desde)}`, filtros.hasta && `Hasta ${fechaReporte(filtros.hasta)}`,
        filtros.nro_factura && `Factura ${filtros.nro_factura}`].filter(Boolean).join(" · "),
      columnas: COLS.map(c => ({ l: c.l, num: !!c.num })),
      filas: filas.map(f => COLS.map(c => (c.k === "fecha" ? fechaReporte(f.fecha) : f[c.k]))),
      totales: COLS.map((c, i) => (i === 0 ? "TOTALES" : c.num ? Number(totales[c.k] || 0) : "")),
      archivo: `Libro_Ventas_${new Date().toISOString().slice(0, 10)}.pdf`,
      ver,
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-h-full" data-testid="libro-ventas-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Libro de Ventas</h1>
          <p className="text-sm text-gray-500 mt-0.5">Las facturas emitidas a clientes, con su desglose de IVA. Es solo informe: las facturas se cargan en Facturación y Cobranza.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
            data-testid="ventas-refrescar"><RefreshCw size={14} /> Actualizar</button>
          <button onClick={exportar} disabled={exportando}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#2E6DA4] text-white hover:bg-[#245A8A] disabled:opacity-50"
            data-testid="ventas-exportar">
            {exportando ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Excel
          </button>
          <button onClick={() => exportarPdf()}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            data-testid="ventas-exportar-pdf">
            <FileText size={14} /> PDF
          </button>
          <button onClick={() => exportarPdf(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            data-testid="ventas-preview">
            <Eye size={14} /> Vista previa
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs font-semibold text-gray-500">Desde
          <input type="date" value={filtros.desde} onChange={e => setFiltros(f => ({ ...f, desde: e.target.value }))}
            className="block border border-gray-200 rounded-lg px-2 py-1.5 text-xs mt-1" data-testid="ventas-desde" />
        </label>
        <label className="text-xs font-semibold text-gray-500">Hasta
          <input type="date" value={filtros.hasta} onChange={e => setFiltros(f => ({ ...f, hasta: e.target.value }))}
            className="block border border-gray-200 rounded-lg px-2 py-1.5 text-xs mt-1" data-testid="ventas-hasta" />
        </label>
        <label className="text-xs font-semibold text-gray-500">N° de factura
          <input value={filtros.nro_factura} onChange={e => setFiltros(f => ({ ...f, nro_factura: e.target.value }))}
            className="block border border-gray-200 rounded-lg px-2 py-1.5 text-xs mt-1 w-40" data-testid="ventas-nro" />
        </label>
      </div>

      {sinDatos > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-center gap-2"
          data-testid="ventas-aviso-sin-datos">
          <AlertTriangle size={16} />
          {sinDatos} {sinDatos === 1 ? "venta no tiene" : "ventas no tienen"} el número de factura o el RUC cargado. Completalos en{" "}
          <Link to="/tesoreria/cobranzas" className="underline font-semibold" data-testid="ventas-ir-facturacion">Facturación y Cobranza</Link> (lápiz «Modificar factura»).
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="ventas-tabla">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {COLS.map(c => (
                  <th key={c.k} className={`px-3 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap ${c.num ? "text-right" : "text-left"}`}>{c.l}</th>
                ))}
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={COLS.length + 1} className="text-center py-12 text-gray-400 text-sm">
                  <Loader2 size={20} className="mx-auto mb-2 animate-spin" /> Cargando…
                </td></tr>
              ) : filas.length === 0 ? (
                <tr><td colSpan={COLS.length + 1} className="text-center py-12 text-gray-400 text-sm">
                  No hay ventas en el período elegido
                </td></tr>
              ) : filas.map(f => (
                <tr key={f._id} className={`border-b border-gray-50 hover:bg-gray-50/40 ${f.sin_datos_fiscales ? "bg-amber-50/40" : ""} ${f.anulada ? "bg-gray-50/70" : ""}`}
                  data-testid={`ventas-fila-${f._id}`}>
                  {COLS.map(c => (
                    <td key={c.k} className={`px-3 py-2.5 text-sm whitespace-nowrap ${c.num ? "text-right tabular-nums" : ""} ${c.mono ? "font-mono text-xs" : ""} ${c.num ? "text-gray-900" : "text-gray-600"}`}>
                      {c.num ? fmt(f[c.k]) : (f.anulada && c.k === "razon_social"
                        ? <span className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 border border-gray-300" data-testid={`ventas-anulada-${f._id}`}>ANULADA</span>   /* Fix HO */
                        : (f[c.k] || "—"))}
                      {/* Fix FG (2026-10-08): factura en USD, en Gs. al cambio de la emisión */}
                      {c.k === "total" && f.moneda === "USD" && (
                        <p className="text-[10px] text-emerald-700" data-testid={`ventas-usd-${f._id}`}>
                          US$ {Number(f.total_usd || 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · 1 USD = Gs. {Math.round(f.tipo_cambio || 0).toLocaleString("es-PY")}
                        </p>
                      )}
                      {/* Fix GK: N° del comprobante de retención, cuando ya se registró */}
                      {c.k === "retencion_iva" && f.retencion_numero && (
                        <p className="text-[10px] text-gray-500 font-mono" data-testid={`ventas-ret-nro-${f._id}`}>N° {f.retencion_numero}</p>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {/* Fix FP: la factura emitida desde el sistema se puede ver para transcribirla (Fix HP: solo lectura) */}
                    {f.factura_emitida && (
                      <button onClick={() => setVistaFactura(f)} className="mr-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#2E6DA4] hover:underline"
                        data-testid={`ventas-factura-${f._id}`}><Receipt size={13} /> Factura</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {!loading && filas.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 font-bold text-gray-800 text-sm">
                  <td className="px-3 py-3" colSpan={6}>Totales ({filas.length})</td>
                  {["grav_10", "iva_10", "exento", "total", "retencion_iva"].map(k => (   /* Fix GK */
                    <td key={k} className="px-3 py-3 text-right tabular-nums whitespace-nowrap" data-testid={`ventas-total-${k}`}>
                      {fmt(totales[k])}
                    </td>
                  ))}
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {vistaFactura && <FacturaVistaModal fila={vistaFactura} onClose={() => setVistaFactura(null)} />}
    </div>
  );
}
